import {
  parseMultilineContent,
  stringifyMultilineContent,
  stringifyVariableAsIdentity,
} from "@repo/app-types/content/plaintext";
import type { MultilineContent } from "@repo/app-types/content/types";
import { standardRecordingSummaryTemplateBody } from "@repo/app-types/entities/messageTemplate";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import type { FC } from "react";
import { useCallback, useMemo } from "react";
import { DiffableField } from "../diffableField.js";

const DESCRIPTION =
  "Call summaries will be generated based on this template and will appear alongside transcripts.";

export interface SummaryTemplateSettingsFormProps {
  body: MultilineContent | null;
  onUpdateBody: (body: MultilineContent | null) => void;
  isReadOnly: boolean;
  /**
   * Live/published body. When provided (i.e. not `undefined`) and it differs
   * from the current body, a "See changes" toggle diffs the draft against it.
   * Leave undefined for no toggle.
   */
  liveBody?: MultilineContent | null;
  /** Show a draft dot next to the label when there are unpublished changes. */
  hasUnpublishedChanges?: boolean;
  /**
   * Fill a bounded parent (the assessment editor's settings pane), with the
   * body scrolling inside it. Leave unset on a flowing page.
   */
  fillsHeight?: boolean;
}

function stringifyBody(body: MultilineContent | null): string {
  return body
    ? stringifyMultilineContent(body, {
        stringifyVariable: stringifyVariableAsIdentity,
      })
    : "";
}

export const SummaryTemplateSettingsForm: FC<
  SummaryTemplateSettingsFormProps
> = ({
  body,
  onUpdateBody,
  isReadOnly,
  liveBody,
  hasUnpublishedChanges,
  fillsHeight = false,
}) => {
  const bodyText = useMemo(() => stringifyBody(body), [body]);

  const handleBodyChange = useCallback(
    (newValue: string) => {
      if (newValue.trim() === "") {
        onUpdateBody(null);
        return;
      }

      onUpdateBody(parseMultilineContent(newValue, { parsesVariables: false }));
    },
    [onUpdateBody],
  );

  return (
    <div className={fillsHeight ? "flex min-h-0 flex-1 flex-col" : undefined}>
      <DiffableField
        label={
          <Label size={Size.SMALL}>
            <span className="flex items-center gap-1.5">
              Summary template
              {hasUnpublishedChanges && (
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              )}
            </span>
          </Label>
        }
        value={bodyText}
        onChange={handleBodyChange}
        originalValue={
          liveBody === undefined ? undefined : stringifyBody(liveBody)
        }
        placeholder={standardRecordingSummaryTemplateBody}
        isReadOnly={isReadOnly}
        description={DESCRIPTION}
        editorClassName={fillsHeight ? "min-h-0 flex-1" : "h-[400px]"}
      />
    </div>
  );
};
